import { env } from "config/env";
import { err } from "neverthrow";
import { getStateAndOtherInfoFromStorage } from "supertokens-web-js/recipe/thirdparty";
import type { ExtraState } from "./types";

export function handleSuperTokensError(error: any, message: string) {
  if (error.isSuperTokensGeneralError) return err(error.message as string);
  return err(message);
}

const EXCLUDED_PARAMS = new Set(["returnTo", "state", "code"]);

export function getForwardingParams(search = globalThis.location.search) {
  const searchParams = new URLSearchParams(search);
  return new URLSearchParams([...searchParams.entries()].filter(([key]) => !EXCLUDED_PARAMS.has(key)));
}

export function forwardParams(url: string) {
  const queryIndex = url.indexOf("?");
  const existingParams = new URLSearchParams(queryIndex === -1 ? undefined : url.slice(queryIndex + 1));
  for (const [key, value] of getForwardingParams().entries()) {
    existingParams.set(key, value);
  }
  const base = queryIndex === -1 ? url : url.slice(0, queryIndex);
  return `${base}?${existingParams.toString()}`;
}

// Auth wizard steps live under the `/auth/$` splat, so they have no typed route to carry search —
// build the href verbatim, re-seeding returnTo (forwardParams drops it) and letting forwardParams
// merge the rest of the current query.
export function authStepHref(path: string) {
  const returnTo = getReturnTo(globalThis.location.search);
  return forwardParams(`${path}?returnTo=${encodeURIComponent(returnTo)}`);
}

export function getReturnTo(
  search = getStateAndOtherInfoFromStorage<ExtraState>()?.search ?? globalThis.location.search,
) {
  return new URLSearchParams(search).get("returnTo") ?? "/";
}

/**
 * Navigate to a post-login destination safely.
 *
 * Why this exists: react-router's useNavigate() is for in-app routing. When
 * given an absolute cross-origin URL (e.g. https://data.qwick.com/) it treats
 * the whole string as a relative pathname and misroutes — which broke
 * redirects back to cross-subdomain tools (data.qwick.com, etc.) after login.
 *
 * Rules:
 *   - Same-origin paths/URLs → react-router navigate (preserves SPA UX for
 *     the existing internal-path callsites).
 *   - Cross-origin URLs under an allow-listed apex (see
 *     REACT_APP_AUTH_ALLOWED_REDIRECT_HOSTS in config/env.ts) →
 *     window.location.replace/assign — hard navigation that react-router
 *     can't do.
 *   - Anything else (hostile or malformed returnTo) → drop to "/". Closes an
 *     open-redirect vector: previously an attacker could link
 *     biz.qwick.com/auth?returnTo=https://evil.com/ and get a post-login
 *     bounce if any callsite ever started honoring absolute URLs.
 *
 * Single callsite: useSession's onLogin (see queries/session.ts). Both OTP
 * and SSO flows funnel through there, so this helper covers both.
 */

function isAllowedRedirectHost(host: string): boolean {
  const lower = host.toLowerCase();
  return env.REACT_APP_AUTH_ALLOWED_REDIRECT_HOSTS.some(
    (allowed) => lower === allowed || lower.endsWith(`.${allowed}`),
  );
}

export async function navigateToReturnTo(
  navigate: (options: { href: string; replace?: boolean }) => Promise<void>,
  returnTo: string | undefined,
  options: { replace?: boolean } = {},
): Promise<void> {
  const fallback = "/";
  const raw = returnTo ?? fallback;

  let parsed: URL;
  try {
    parsed = new URL(raw, globalThis.location.origin);
  } catch {
    await navigate({ href: fallback, replace: options.replace });
    return;
  }

  if (parsed.origin === globalThis.location.origin) {
    await navigate({ href: parsed.pathname + parsed.search + parsed.hash, replace: options.replace });
    return;
  }

  if (isAllowedRedirectHost(parsed.hostname) && parsed.protocol === "https:") {
    if (options.replace) globalThis.location.replace(parsed.toString());
    else globalThis.location.assign(parsed.toString());
    return;
  }

  // Unknown / non-allowlisted origin — drop it.
  await navigate({ href: fallback, replace: options.replace });
}
